import { createServerFn } from "@tanstack/react-start";

import { CONTENT_TTL_SECONDS } from "./cachePolicy";
import { readStamp, stillCurrent } from "./contentGeneration";
import { isCmsPreview } from "./preview";
import { contentSource } from "./source";
import type { Author, BlogPost, BlogPostSummary, Category } from "./types";

/**
 * What routes call.
 *
 * Server functions rather than plain loader fetches: a route loader re-runs in
 * the browser on client-side navigation, which would put a CMS token
 * in the client bundle. The Start plugin replaces these handlers with an RPC
 * call on the client, so the token and the snapshot stay on the server.
 */

/** The same TTL the response carries, so the two cannot drift apart. */
const CACHE_TTL_MS = CONTENT_TTL_SECONDS * 1000;

let cache:
  | { loadedAt: number; generation: number; posts: Promise<BlogPost[]> }
  | undefined;

/**
 * The adapter is resolved inside the handler rather than at module scope: a
 * static import survives into the client chunk even though the handler bodies
 * around it do not, measured at 28kB of DOMPurify shipped to browsers that
 * never call it. `contentSource` keeps that `import()` behind it.
 */
async function loadPosts(): Promise<BlogPost[]> {
  const { loadBlogPosts } = await contentSource();
  return loadBlogPosts();
}

/**
 * One fetch per TTL rather than one per request. Prerendering every post would
 * otherwise issue three API calls per page, several hundred for one build. A
 * publish ends the copy early (`contentGeneration.ts`).
 */
function cachedPosts(): Promise<BlogPost[]> {
  // A preview request reads the drafts, fresh, and holds nothing: the copy
  // every other request shares is the published one and stays so.
  if (isCmsPreview()) {
    return loadPosts();
  }
  if (!stillCurrent(cache, CACHE_TTL_MS)) {
    const posts = loadPosts().catch((error: unknown) => {
      cache = undefined;
      throw error;
    });
    cache = { ...readStamp(), posts };
  }
  return cache.posts;
}

export const getBlogIndex = createServerFn({ method: "GET" }).handler(
  async (): Promise<BlogPostSummary[]> => {
    const { toSummary } = await contentSource();
    return (await cachedPosts()).map(toSummary);
  }
);

export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((slug: unknown): string => String(slug ?? ""))
  .handler(
    async ({ data }): Promise<BlogPost | null> =>
      (await cachedPosts()).find((post) => post.slug === data) ?? null
  );

/**
 * The two listings the published site derives from a post's own fields rather
 * than from a collection of their own: `/author/:slug` and
 * `/blog-categories/:slug`.
 *
 * Both read the copy denormalised onto each post, `Author` already carries the
 * bio the author page prints as its meta description, and `Category` the blurb
 * its listing does, so neither needs a method on `ContentSource`, and adding
 * them costs no adapter work when the CMS changes.
 */
export type AuthorPage = { author: Author; posts: BlogPostSummary[] };
export type CategoryPage = { category: Category; posts: BlogPostSummary[] };

export const getAuthorPage = createServerFn({ method: "GET" })
  .inputValidator((slug: unknown): string => String(slug ?? ""))
  .handler(async ({ data }): Promise<AuthorPage | null> => {
    const { toSummary } = await contentSource();
    const written = (await cachedPosts()).filter(
      (post) => post.author?.slug === data
    );
    const author = written[0]?.author;
    return author ? { author, posts: written.map(toSummary) } : null;
  });

export const getCategoryPage = createServerFn({ method: "GET" })
  .inputValidator((slug: unknown): string => String(slug ?? ""))
  .handler(async ({ data }): Promise<CategoryPage | null> => {
    const { toSummary } = await contentSource();
    const filed = (await cachedPosts()).filter(
      (post) => post.category?.slug === data
    );
    const category = filed[0]?.category;
    return category ? { category, posts: filed.map(toSummary) } : null;
  });
